import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messagerieService } from '../services/messagerie.service';
import { useToast } from './use-toast';

// Clés de requête pour React Query
export const MESSAGERIE_QUERY_KEYS = {
  received: (params) => ['messagerie', 'received', params],
  sent: (params) => ['messagerie', 'sent', params],
  message: (id) => ['messagerie', 'message', id],
  employees: ['messagerie', 'employees'],
  stats: ['messagerie', 'stats'],
};

/**
 * Hook pour récupérer les messages reçus
 */
export const useReceivedMessages = (params = {}) => {
  return useQuery({
    queryKey: MESSAGERIE_QUERY_KEYS.received(params),
    queryFn: () => messagerieService.getReceivedMessages(params),
    keepPreviousData: true,
    staleTime: 30000, // 30 secondes
  });
};

/**
 * Hook pour récupérer les messages envoyés
 */
export const useSentMessages = (params = {}) => {
  return useQuery({
    queryKey: MESSAGERIE_QUERY_KEYS.sent(params),
    queryFn: () => messagerieService.getSentMessages(params),
    keepPreviousData: true,
    staleTime: 30000,
  });
};

/**
 * Hook pour récupérer un message spécifique
 */
export const useMessage = (messageId) => {
  return useQuery({
    queryKey: MESSAGERIE_QUERY_KEYS.message(messageId),
    queryFn: () => messagerieService.getMessageById(messageId),
    enabled: !!messageId,
  });
};

/**
 * Hook pour récupérer les employés
 */
export const useEmployees = () => {
  return useQuery({
    queryKey: MESSAGERIE_QUERY_KEYS.employees,
    queryFn: messagerieService.getEmployees,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour récupérer les statistiques
 */
export const useMessageStats = () => {
  return useQuery({
    queryKey: MESSAGERIE_QUERY_KEYS.stats,
    queryFn: messagerieService.getMessageStats,
    refetchInterval: 60000, // Actualiser toutes les minutes
  });
};

/**
 * Hook pour créer un nouveau message
 */
export const useCreateMessage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ messageData, file }) => messagerieService.createMessage(messageData, file),
    onSuccess: () => {
      // Invalider les caches des messages envoyés et reçus
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'sent'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'received'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'stats'] });
      
      toast({
        title: "Message envoyé",
        description: "Votre message a été envoyé avec succès.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Erreur lors de l'envoi du message.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook pour répondre à un message
 */
export const useReplyToMessage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ messageId, reponse }) => messagerieService.replyToMessage(messageId, reponse),
    onSuccess: (data, variables) => {
      // Invalider le cache du message spécifique
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'message', variables.messageId] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'received'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'stats'] });
      
      toast({
        title: "Réponse envoyée",
        description: "Votre réponse a été ajoutée avec succès.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Erreur lors de l'envoi de la réponse.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook pour mettre à jour le statut d'un message
 */
export const useUpdateMessageStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ messageId, statut }) => messagerieService.updateMessageStatus(messageId, statut),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'message', variables.messageId] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'received'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'sent'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'stats'] });
      
      toast({
        title: "Statut mis à jour",
        description: "Le statut du message a été mis à jour.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Erreur lors de la mise à jour du statut.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook pour supprimer un message
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: messagerieService.deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'sent'] });
      queryClient.invalidateQueries({ queryKey: ['messagerie', 'stats'] });
      
      toast({
        title: "Message supprimé",
        description: "Le message a été supprimé avec succès.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Erreur lors de la suppression du message.",
        variant: "destructive",
      });
    },
  });
};
